import { Component, effect, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Table, TableModule } from 'primeng/table';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { TableComponent } from '../../../../core/components/table/table.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { RaisonImmobilisation } from './raison-immobilisation.model';
import { LieuImmobilisation } from './lieu-immobilisation.model';
import { ReferentialsImmobilisationDemandeService } from './referentials-immobilisation-demande.service';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { InputTextModule } from 'primeng/inputtext';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { AddRaisonImmobilisationComponent } from './add-raison-immobilisation/add-raison-immobilisation.component';
import { AddLieuImmobilisationComponent } from './add-lieu-immobilisation/add-lieu-immobilisation.component';
import { TooltipModule } from 'primeng/tooltip';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-immobilisation-demande',
  standalone: true,
  imports: [CommonModule, TableModule, ReactiveFormsModule, ButtonModule, InputTextModule, FormsModule, ToastModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-immobilisation-demande.component.html',
  styleUrl: './referentials-immobilisation-demande.component.css',
})
export class ReferentialsImmobilisationDemandeComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('raisonImmobilisationTable') table!: Table;

  userHasReadWritePermission: boolean = false;

  raisonResult!: RaisonImmobilisation[];
  lieuResult!: LieuImmobilisation[];

  selectedRaisonImmobilisation?: RaisonImmobilisation;
  selectedLieuImmobilisation?: LieuImmobilisation;

  ref: DynamicDialogRef | undefined;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];
  loading: boolean = false;

  raisonTotalRecords: number = 0;
  lieuTotalRecords: number = 0;
  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedRaisonImmobilisation: { [s: string]: RaisonImmobilisation } = {};
  clonedLieuImmobilisation: { [s: string]: LieuImmobilisation } = {};
  lieuImmobilisationForm = this.formBuilder.group({
    id: this.formBuilder.control<Number | null>(null),
    lieu: ['']
  });
  raisonImmobilisationForm = this.formBuilder.group({
    id: this.formBuilder.control<Number | null>(null),
    raison: ['']
  });

  constructor(
    private immobilisationDemandeService: ReferentialsImmobilisationDemandeService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    private formBuilder: FormBuilder,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadRaisonImmobilisation();
    this.loadLieuImmobilisation();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadRaisonImmobilisation() {
    this.loading = true;
    this.immobilisationDemandeService
      .getRaisonImmobilisation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.raisonResult = data;
        this.raisonTotalRecords = this.raisonResult.length;
        this.loading = false;
      });
  }

  private loadLieuImmobilisation() {
    this.loading = true;
    this.immobilisationDemandeService
      .getLieuImmobilisation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.lieuResult = data;
        this.lieuTotalRecords = this.lieuResult.length;
        this.loading = false;
      });
  }

  // Raison Immobilsation
  ajouterRaisonImmobilisation() {
    this.ref = this.dialogService.open(AddRaisonImmobilisationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadRaisonImmobilisation();
      }
    });
  }

  modifierRaisonImmobilisation(raisonImmobilisation: RaisonImmobilisation, index: number) {
    this.editRowIndex = index;
    this.editingLine = raisonImmobilisation;
    this.selectedRaisonImmobilisation = raisonImmobilisation;
    const id = raisonImmobilisation.id;
    this.raisonImmobilisationForm.controls.id.patchValue(raisonImmobilisation.id);
    this.raisonImmobilisationForm.controls.raison.patchValue(raisonImmobilisation.raison);
    this.clonedRaisonImmobilisation[id.toString()] = { ...raisonImmobilisation };
  }

  validerModifsRaisonImmobilisation(raisonImmobilisation: RaisonImmobilisation) {
    this.immobilisationDemandeService.updateRaisonImmobilisation(this.raisonImmobilisationForm.value as RaisonImmobilisation).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
        this.loadRaisonImmobilisation();
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsRaisonImmobilisation(raisonImmobilisation: RaisonImmobilisation, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = raisonImmobilisation.id;
    this.raisonImmobilisationForm.controls.id.patchValue(raisonImmobilisation.id);
    this.raisonImmobilisationForm.controls.raison.patchValue(raisonImmobilisation.raison);
    this.raisonResult.filter(r=>r.id===id).map(raison => {
      raison = this.clonedRaisonImmobilisation[id.toString()];
    });
    delete this.clonedRaisonImmobilisation[id.toString()];
  }

  // Lieu Immobilsation
  ajouterLieuImmobilisation() {
    this.ref = this.dialogService.open(AddLieuImmobilisationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadLieuImmobilisation();
      }
    });
  }

  modifierLieuImmobilisation(lieuImmobilisation: LieuImmobilisation, index: number) {
    this.editRowIndex = index;
    this.editingLine = lieuImmobilisation;
    this.selectedLieuImmobilisation = lieuImmobilisation;
    const id = lieuImmobilisation.id;
    this.lieuImmobilisationForm.controls.id.patchValue(lieuImmobilisation.id);
    this.lieuImmobilisationForm.controls.lieu.patchValue(lieuImmobilisation.lieu);
    this.clonedLieuImmobilisation[id.toString()] = { ...lieuImmobilisation };
  }

  validerModifsLieuImmobilisation(lieuImmobilisation: LieuImmobilisation) {
    this.immobilisationDemandeService.updateLieuImmobilisation(this.lieuImmobilisationForm.value as LieuImmobilisation).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
        this.loadLieuImmobilisation();
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsLieuImmobilisation(lieuImmobilisation: LieuImmobilisation, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = lieuImmobilisation.id;
    this.lieuImmobilisationForm.controls.id.patchValue(lieuImmobilisation.id);
    this.lieuImmobilisationForm.controls.lieu.patchValue(lieuImmobilisation.lieu);
    this.lieuResult.filter(l=>l.id===id).map(lieu => {
      lieu = this.clonedLieuImmobilisation[id.toString()];
    });
    delete this.clonedLieuImmobilisation[id.toString()];
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: this.translocoService.translate('info.popup.titre.defaut'), detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: errorMessage, life: 3000 });
  }

  onRaisonImmobilisationSelect(raisonImmobilisaion: RaisonImmobilisation) {
    this.selectedRaisonImmobilisation = raisonImmobilisaion;
  }

  onLieuImmobilisationSelect(lieuImmobilisation: LieuImmobilisation) {
    this.selectedLieuImmobilisation = lieuImmobilisation;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
